import { useMemo, useState } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';

const COLLAPSED_PILL_COUNT = 10;

type GenesGtpClassFilterProps = {
  classNames: string[];
  hiddenClassNames: string[];
  classDotColors?: Record<string, string>;
  onToggle: (className: string) => void;
  onShowAll: () => void;
  onShowNone: () => void;
  onReset: () => void;
};

export function GenesGtpClassFilter({
  classNames,
  hiddenClassNames,
  classDotColors = {},
  onToggle,
  onShowAll,
  onShowNone,
  onReset,
}: GenesGtpClassFilterProps) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const hidden = useMemo(() => new Set(hiddenClassNames), [hiddenClassNames]);
  const shownCount = classNames.length - hiddenClassNames.filter(name => classNames.includes(name)).length;
  const allShown = shownCount === classNames.length;
  const noneShown = shownCount === 0;
  const canReset = !allShown || query.trim().length > 0 || expanded;

  const filteredClassNames = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return classNames;
    return classNames.filter(name => name.toLowerCase().includes(needle));
  }, [classNames, query]);

  const isSearchActive = query.trim().length > 0;
  const visiblePills =
    isSearchActive || expanded ? filteredClassNames : filteredClassNames.slice(0, COLLAPSED_PILL_COUNT);
  const hiddenPillCount = filteredClassNames.length - visiblePills.length;
  const canToggleCollapse = !isSearchActive && filteredClassNames.length > COLLAPSED_PILL_COUNT;

  return (
    <div className="gtp-heatmap-filter">
      <div className="gtp-heatmap-filter__header">
        <div className="gtp-heatmap-filter__title-wrap">
          <h5 className="gtp-heatmap-filter__title">Disease classes</h5>
          <span className="gtp-heatmap-filter__count">
            {shownCount} / {classNames.length} shown
          </span>
        </div>
        <div className="gtp-heatmap-filter__controls">
          <label className="gtp-heatmap-filter__search">
            <MagnifyingGlassIcon className="gtp-heatmap-filter__search-icon" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Filter classes"
              aria-label="Filter classes"
              autoComplete="off"
            />
          </label>
          <button
            type="button"
            className="gtp-heatmap-filter__action"
            onClick={onShowAll}
            disabled={allShown}
          >
            All
          </button>
          <button
            type="button"
            className="gtp-heatmap-filter__action"
            onClick={onShowNone}
            disabled={noneShown}
          >
            None
          </button>
          <button
            type="button"
            className="gtp-heatmap-filter__action"
            disabled={!canReset}
            onClick={() => {
              setQuery('');
              setExpanded(false);
              onReset();
            }}
          >
            <ArrowPathIcon className="gtp-heatmap-filter__reset-icon" aria-hidden />
            Reset
          </button>
        </div>
      </div>

      <div className="gtp-heatmap-filter__pills" aria-label="Disease class filters">
        {visiblePills.map(className => {
          const selected = !hidden.has(className);
          return (
            <button
              key={className}
              type="button"
              className={`gtp-heatmap-filter__pill${selected ? ' is-selected' : ''}`}
              aria-pressed={selected}
              aria-label={selected ? `Hide ${className}` : `Show ${className}`}
              title={className}
              onClick={() => onToggle(className)}
            >
              <span
                className="gtp-heatmap-filter__pill-dot"
                style={{ backgroundColor: classDotColors[className] || '#f7f7f7' }}
                aria-hidden
              />
              <span className="gtp-heatmap-filter__pill-label">{className}</span>
              {selected ? (
                <CheckIcon className="gtp-heatmap-filter__pill-icon" aria-hidden />
              ) : (
                <XMarkIcon className="gtp-heatmap-filter__pill-icon" aria-hidden />
              )}
            </button>
          );
        })}
      </div>

      {canToggleCollapse ? (
        <button
          type="button"
          className="gtp-heatmap-filter__more"
          onClick={() => setExpanded(current => !current)}
        >
          {expanded ? 'Show fewer classes' : `Show ${hiddenPillCount} more classes`}
          {expanded ? (
            <ChevronUpIcon className="gtp-heatmap-filter__more-icon" aria-hidden />
          ) : (
            <ChevronDownIcon className="gtp-heatmap-filter__more-icon" aria-hidden />
          )}
        </button>
      ) : null}
    </div>
  );
}
